import { Ajuda } from '../../design/Ajuda';
import '../central/central.css';
import './giro.css';

/**
 * A subnav do módulo Giro (telas 16–18): uma entrada na sidebar e a subnav com as áreas.
 * São 15 telas: em vez de uma parede de abas em várias linhas, três grupos (o dia, o controle e
 * os ajustes) e só as abas do grupo aberto. Cada tela tem o "(?)" dizendo para que serve.
 */

type Grupo = 'dia' | 'controle' | 'ajustes';

const GRUPOS: readonly { grupo: Grupo; rotulo: string }[] = [
  { grupo: 'dia', rotulo: 'Dia a dia' },
  { grupo: 'controle', rotulo: 'Controle' },
  { grupo: 'ajustes', rotulo: 'Ajustes' },
];

const ABAS: readonly { rota: string; rotulo: string; grupo: Grupo; ajuda: string }[] = [
  {
    rota: 'giro',
    rotulo: 'Visão geral',
    grupo: 'dia',
    ajuda:
      'O resumo do Giro de hoje: quantos produtos entraram, quanto já foi contado e reposto, como está cada setor e os alertas.',
  },
  {
    rota: 'giro-operacao',
    rotulo: 'Operação de hoje',
    grupo: 'dia',
    ajuda:
      'Os blocos de hoje, setor por setor: quem está com cada bloco, quanto já contou e o botão para trocar a pessoa de um bloco.',
  },
  {
    rota: 'giro-geracao',
    rotulo: 'Geração',
    grupo: 'dia',
    ajuda:
      'Onde o Giro do dia é montado: escolhe o dia e os setores, simula, gera e publica. Nada chega no balconista antes de publicar.',
  },
  {
    rota: 'giro-equipe',
    rotulo: 'Equipe & presença',
    grupo: 'dia',
    ajuda:
      'Quem está na loja hoje (presente, meio período ou ausente), quem é de cada setor e o calendário de folgas. Ausente não recebe bloco.',
  },
  {
    rota: 'giro-ocorrencias',
    rotulo: 'Ocorrências',
    grupo: 'dia',
    ajuda:
      'Problemas que a equipe anotou no corredor durante o Giro (prateleira quebrada, etiqueta errada…). Não mudam a contagem; fechar pede o que foi feito.',
  },
  {
    rota: 'giro-excecoes',
    rotulo: 'Exceções',
    grupo: 'dia',
    ajuda:
      'Produtos que venderam ontem mas ficaram fora do Giro de hoje e o porquê: estoque zero, estoque sem leitura do HOS, sem setor ou sem cadastro.',
  },
  {
    rota: 'giro-bloqueados',
    rotulo: 'Bloqueados',
    grupo: 'controle',
    ajuda:
      'Erros de reposição por balconista: o auditor achou o que ele disse que não tinha. Passou do limite da regra, as vendas dele ficam bloqueadas no dia seguinte.',
  },
  {
    rota: 'giro-relatorio',
    rotulo: 'Relatório de erros',
    grupo: 'controle',
    ajuda:
      'Quanto cada balconista está errando no Giro e em quê: produto a produto (prateleira, pedido, estoque, reposto), fotos da prateleira e da cesta, o motivo dos bloqueios e quanto o Igor e o Felipe conferiram e arrumaram. Filtra por datas, balconista, situação, setor e produto.',
  },
  {
    rota: 'giro-capacidades',
    rotulo: 'Não cabe & bloqueios',
    grupo: 'controle',
    ajuda:
      'Produtos que a equipe disse que não cabem na prateleira, com o giro máximo e a contagem × HOS, para o gestor confirmar ou recusar. Também libera bloqueios.',
  },
  {
    rota: 'giro-rupturas',
    rotulo: 'Rupturas',
    grupo: 'controle',
    ajuda:
      'Produtos que venderam mas o estoque do HOS estava zerado: não dá para repor, então ficam fora da missão e aparecem aqui para compra.',
  },
  {
    rota: 'giro-painel',
    rotulo: 'Painel',
    grupo: 'controle',
    ajuda:
      'Os números do período (7, 30 ou 90 dias): quanto de cada setor já passou pelo Giro, o tempo de cada balconista e os produtos que mais giram.',
  },
  {
    rota: 'giro-max',
    rotulo: 'Giro Max',
    grupo: 'controle',
    ajuda:
      'Produtos que venderam muito num único dia. Serve para saber quanto precisa caber na prateleira. Só para donos (mostra vendas por vendedor).',
  },
  {
    rota: 'giro-historico',
    rotulo: 'Histórico',
    grupo: 'controle',
    ajuda:
      'As operações dos dias anteriores, do jeito que foram naquele dia (não recalcula com a configuração de hoje).',
  },
  {
    rota: 'giro-setores',
    rotulo: 'Setores & blocos',
    grupo: 'ajustes',
    ajuda:
      'Cadastro dos setores do Giro: que grupos de produto entram, em quantos blocos o setor é dividido e quem trabalha em cada setor.',
  },
  {
    rota: 'giro-rodizio',
    rotulo: 'Rodízio',
    grupo: 'ajustes',
    ajuda:
      'Mostra por quais blocos cada pessoa já passou no ciclo. Cada um roda por todos os blocos do setor; só bloco terminado inteiro conta a vez.',
  },
  {
    rota: 'giro-config',
    rotulo: 'Configurações',
    grupo: 'ajustes',
    ajuda:
      'As regras do Giro: horário da geração automática, alvo na prateleira, prazo da divergência, fotos, horário de cada balconista. Toda mudança pede motivo e vira versão nova.',
  },
];

export function SubnavDoGiro({
  atual,
  excecoes,
  direita,
}: {
  atual: string;
  excecoes?: number | undefined;
  direita?: string | undefined;
}) {
  const aberta = ABAS.find((a) => a.rota === atual);
  const grupo: Grupo = aberta?.grupo ?? 'dia';
  return (
    <nav className="cv-subnav gr-subnav" aria-label="Giro de Prateleira">
      <div className="gr-subnav-grupos" role="group" aria-label="Áreas do Giro">
        {GRUPOS.map((g) => {
          const primeira = ABAS.find((a) => a.grupo === g.grupo);
          return (
            <a
              key={g.grupo}
              href={`#/${primeira?.rota ?? 'giro'}`}
              className={g.grupo === grupo ? 'gr-subnav-grupo-sel' : ''}
              aria-current={g.grupo === grupo ? 'true' : undefined}
            >
              {g.rotulo}
            </a>
          );
        })}
      </div>
      <div className="gr-subnav-abas">
        {ABAS.filter((a) => a.grupo === grupo).map((a) => (
          <span key={a.rota} className="cv-subnav-item">
            <a
              href={`#/${a.rota}`}
              className={atual === a.rota ? 'cv-sel' : ''}
              aria-current={atual === a.rota ? 'page' : undefined}
              title={a.ajuda}
              // No celular a fileira rola: a aba aberta aparece sem a pessoa procurar.
              ref={
                atual === a.rota
                  ? (el) => el?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
                  : undefined
              }
            >
              {a.rotulo}
              {a.rota === 'giro-excecoes' && excecoes !== undefined && excecoes > 0 && (
                <span className="gr-contador">{excecoes}</span>
              )}
            </a>
            {atual === a.rota && <Ajuda rotulo={a.rotulo}>{a.ajuda}</Ajuda>}
          </span>
        ))}
      </div>
      {direita && <span className="cv-subnav-direita gr-subnav-direita">{direita}</span>}
    </nav>
  );
}
